import { useNavigate } from 'react-router-dom';
import { useRef, useEffect, useCallback } from 'react';
import { Film, ArrowRight, Sparkles, Users, Layers, Clapperboard, Upload, Wand2 } from 'lucide-react';

// ── Intersection Observer hook ──
function useReveal(options = {}) {
  const ref = useRef(null);
  const observe = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Stagger children with .reveal class
          const children = el.querySelectorAll('.reveal');
          children.forEach((child, i) => {
            child.style.animationDelay = `${i * 120}ms`;
            child.classList.add('revealed');
          });
          // Also reveal the container itself
          el.classList.add('revealed');
          observer.unobserve(el);
        }
      },
      { threshold: options.threshold ?? 0.15, rootMargin: options.rootMargin ?? '0px 0px -60px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  useEffect(() => {
    const cleanup = observe();
    return cleanup;
  }, [observe]);

  return ref;
}

const PROVIDERS = ['Kling 3.0', 'Seedance 2.5', 'Veo 3.1', 'Gemini', 'OpenAI', 'ElevenLabs'];

const FEATURES = [
  {
    title: 'AI Script Analysis',
    desc: 'Upload any script or document. Our AI extracts every character, setting, and scene with exhaustive detail — ready for production.',
    icon: Sparkles,
    span: 'md:col-span-7',
    img: 'https://images.pexels.com/photos/10075566/pexels-photo-10075566.jpeg',
  },
  {
    title: 'Multi-Provider Video',
    desc: 'Generate with Kling 3.0, Seedance 2.5, or Veo 3.1. First frame, last frame, native audio — up to 30-second clips with synced sound, all from one interface.',
    icon: Clapperboard,
    span: 'md:col-span-5',
    img: 'https://images.pexels.com/photos/5829130/pexels-photo-5829130.jpeg',
  },
  {
    title: 'Team Workspaces',
    desc: 'Collaborate with your team. Shared API keys, role-based access, and team-scoped projects.',
    icon: Users,
    span: 'md:col-span-5',
  },
  {
    title: 'Scene-by-Scene Pipeline',
    desc: 'Cast, references, frames, audio, video — a structured 6-step workflow for every scene. Drag and reorder in the storyboard.',
    icon: Layers,
    span: 'md:col-span-7',
  },
];

const STEPS = [
  { num: '01', title: 'Upload Your Script', desc: 'PDF, DOCX, or plain text. Our AI reads it all.' },
  { num: '02', title: 'AI Extracts Everything', desc: 'Characters, scenes, settings, mood — extracted and organized automatically.' },
  { num: '03', title: 'Generate Scene by Scene', desc: 'Cast your characters, set references, generate frames and video with AI.' },
  { num: '04', title: 'Preview & Export', desc: 'Storyboard your scenes, reorder, rough cut preview, and export.' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const featuresRef = useReveal();
  const pipelineRef = useReveal();
  const ctaRef = useReveal({ threshold: 0.3 });
  const marqueeRef = useReveal({ threshold: 0.5 });

  return (
    <div className="min-h-screen bg-[#060608] text-white overflow-x-hidden" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#060608]/80 backdrop-blur-xl border-b border-white/5 h-20 flex items-center" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FF4500] rounded-xl flex items-center justify-center">
              <Film className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AvantGarde Media
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-[#8A8A93] hover:text-white transition-colors">Features</a>
            <a href="#models" className="text-sm text-[#8A8A93] hover:text-white transition-colors">Models</a>
            <a href="#pipeline" className="text-sm text-[#8A8A93] hover:text-white transition-colors">Pipeline</a>
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="landing-login-btn" onClick={() => navigate('/login')}
              className="text-sm text-[#8A8A93] hover:text-white transition-colors px-4 py-2">
              Sign In
            </button>
            <button data-testid="landing-signup-btn" onClick={() => navigate('/login')}
              className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold text-sm py-2.5 px-6 rounded-none transition-all duration-300">
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero — staggered load animation */}
      <section className="relative pt-32 pb-24 sm:pt-44 sm:pb-32 overflow-hidden" data-testid="hero-section">
        {/* Atmospheric glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#FF4500]/[0.06] rounded-full blur-[120px] glow-pulse" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#060608] to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
          <div className="max-w-4xl">
            <p className="hero-reveal font-mono text-xs tracking-[0.3em] uppercase text-[#FF4500] mb-6" style={{ animationDelay: '0.1s' }} data-testid="hero-eyebrow">
              AI Video Production Pipeline
            </p>
            <h1 className="hero-reveal text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[0.95] text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif', animationDelay: '0.25s' }}>
              Manage your full AI video production pipeline
              <span className="text-[#FF4500]"> in one place</span>
            </h1>
            <p className="hero-reveal text-lg sm:text-xl text-[#8A8A93] font-light leading-relaxed max-w-2xl mb-10" style={{ animationDelay: '0.4s' }}>
              From script to screen. Upload your document, let AI analyze every scene and character, then generate cinematic video with the world's best AI models.
            </p>
            <div className="hero-reveal flex items-center gap-4 flex-wrap" style={{ animationDelay: '0.55s' }}>
              <button data-testid="hero-cta" onClick={() => navigate('/login')}
                className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold py-4 px-8 rounded-none transition-all duration-300 flex items-center gap-2 text-base group">
                Start Creating <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="#pipeline" data-testid="hero-secondary-cta"
                className="bg-transparent border border-white/10 hover:border-white/30 text-white font-medium py-4 px-8 rounded-none transition-all duration-300 text-base">
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Provider Marquee */}
      <section ref={marqueeRef} id="models" className="border-y border-white/5 py-8 overflow-hidden reveal reveal-up" data-testid="provider-marquee">
        <div className="animate-marquee flex gap-16 items-center whitespace-nowrap">
          {[...PROVIDERS, ...PROVIDERS, ...PROVIDERS].map((name, i) => (
            <span key={i} className="text-4xl sm:text-5xl font-black tracking-tighter text-transparent select-none" style={{
              WebkitTextStroke: '1px rgba(255,255,255,0.12)',
              fontFamily: 'Outfit, sans-serif',
            }}>
              {name}
            </span>
          ))}
        </div>
      </section>

      {/* Features Bento Grid */}
      <section ref={featuresRef} id="features" className="py-24 sm:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="reveal reveal-up font-mono text-xs tracking-[0.3em] uppercase text-[#FF4500] mb-4">Capabilities</p>
          <h2 className="reveal reveal-up text-3xl sm:text-4xl font-bold tracking-tight text-white mb-16" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Everything you need for AI video production
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`feature-card-${i}`}
                className={`reveal reveal-scale ${f.span} bg-[#14141A] border border-white/5 p-8 md:p-10 relative overflow-hidden group hover:border-[#FF4500]/20 transition-colors duration-500`}>
                {f.img && (
                  <div className="absolute inset-0 opacity-[0.07] group-hover:opacity-[0.12] transition-opacity duration-700">
                    <img src={f.img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="relative">
                  <div className="w-10 h-10 rounded-lg bg-[#FF4500]/10 flex items-center justify-center mb-5">
                    <f.icon className="w-5 h-5 text-[#FF4500]" />
                  </div>
                  <h3 className="text-xl font-semibold tracking-tight text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {f.title}
                  </h3>
                  <p className="text-sm text-[#8A8A93] font-light leading-relaxed max-w-md">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section ref={pipelineRef} id="pipeline" className="py-24 sm:py-32 border-t border-white/5" data-testid="pipeline-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="reveal reveal-up font-mono text-xs tracking-[0.3em] uppercase text-[#FF4500] mb-4">Pipeline</p>
          <h2 className="reveal reveal-up text-3xl sm:text-4xl font-bold tracking-tight text-white mb-16" style={{ fontFamily: 'Outfit, sans-serif' }}>
            From script to screen in four steps
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0">
            {/* Left: steps */}
            <div className="relative">
              {/* Tracing beam */}
              <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gradient-to-b from-[#FF4500]/40 via-[#FF4500]/10 to-transparent" />

              <div className="space-y-12">
                {STEPS.map((step, i) => (
                  <div key={i} className="reveal reveal-left flex gap-6 group" data-testid={`step-${i}`}>
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-[#14141A] border border-white/10 group-hover:border-[#FF4500]/40 flex items-center justify-center text-xs font-mono text-[#FF4500] transition-colors duration-300 z-10 relative">
                        {step.num}
                      </div>
                    </div>
                    <div className="pt-1.5">
                      <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                      <p className="text-sm text-[#8A8A93] font-light leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: visual */}
            <div className="hidden md:flex items-center justify-center">
              <div className="reveal reveal-scale relative w-full max-w-sm">
                <div className="bg-[#14141A] border border-white/5 rounded-lg p-6 space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[#FF4500]/10 flex items-center justify-center">
                      <Upload className="w-4 h-4 text-[#FF4500]" />
                    </div>
                    <span className="text-sm font-medium text-white">script_final_v3.pdf</span>
                  </div>
                  <div className="space-y-2">
                    {['12 characters extracted', '24 scenes identified', 'Film noir, 1940s aesthetic'].map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-[#8A8A93]">
                        <Wand2 className="w-3 h-3 text-[#FF4500]" /> {t}
                      </div>
                    ))}
                  </div>
                  <div className="pt-3 border-t border-white/5 flex gap-2">
                    {['Kling 3.0', 'Seedance', 'Veo'].map((p, i) => (
                      <span key={i} className="text-[10px] px-2 py-1 rounded bg-white/5 text-[#8A8A93]">{p}</span>
                    ))}
                  </div>
                </div>
                {/* Glow */}
                <div className="absolute -inset-8 bg-[#FF4500]/[0.03] rounded-2xl blur-2xl pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaRef} className="py-24 sm:py-32 border-t border-white/5" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="reveal reveal-up text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to direct?
          </h2>
          <p className="reveal reveal-up text-lg text-[#8A8A93] font-light mb-10 max-w-xl mx-auto">
            Join creators using AI to produce cinematic video at scale. No credit card required.
          </p>
          <div className="reveal reveal-up">
            <button data-testid="cta-signup" onClick={() => navigate('/login')}
              className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold py-4 px-10 rounded-none transition-all duration-300 text-base inline-flex items-center gap-2 group">
              Get Started Free <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-[#FF4500] rounded-lg flex items-center justify-center">
                <Film className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>AvantGarde Media</span>
            </div>
            <p className="text-xs text-[#55555C]">&copy; 2026 AvantGarde Media. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
